'use client';

import { useState } from 'react';
import { GroupedCard, type GroupedCardItem, type GroupedCardProps } from '@pixum/grouped-card';
import { TEXT_VARIANT } from '@pixum/text';
import type { Constant, LinkNode, GroupedCardNode, GroupedCardItemNode, Device } from 'data-layer';
import { PRODUCT_ATTRIBUTE_STATUS_VALUES } from 'data-layer/src/lib/constants';
import { ButtonWithAddToCart } from '../../buttonCollection/variants/ButtonWithAddToCart';
import { ButtonVariants, ButtonSizes } from '@pixum/button';
import { Image, IMAGE_LOADING, IMAGE_TRIM } from '../../image';
import { RichTextBody, isRichTextEmpty } from '../../richtText';
import { getIconByName } from '../../icons';
import { navigateTo, isLinkType } from 'utils';
import { LINK_TYPES } from 'data-layer/constants';
import { useDialogStore, type DialogStore } from 'stores/dialog';

interface GroupedCardComponentProps {
  groupedCard: GroupedCardNode;
  device: Device;
  constants?: Constant;
}

/**
 * Creates a slide-label function from a template string.
 * The template may contain {index} and {total} as placeholders.
 * E.g. "Seite {index} von {total}" → (1, 5) => "Seite 1 von 5"
 */
function parseSlideLabelTemplate(template: string): (index: number, total: number) => string {
  return (index: number, total: number) => template.replace('{index}', String(index)).replace('{total}', String(total));
}

/**
 * Generates the primary action configuration for a grouped card item.
 * Returns a button component for add-to-cart links, or text action for regular links.
 *
 * @param link - The primary link node with action details
 * @param device - Current device type (mobile/desktop)
 * @param constants - Application constants (optional, passed through to ButtonWithAddToCart)
 * @returns Action object with either children (button) or text
 */
function getActionObject(
  link: LinkNode | undefined,
  device: Device,
  constants: Constant | undefined,
  openDialog: DialogStore['openDialog']
): GroupedCardItem['action'] {
  // Add-to-cart links don't need a URL (product is identified via productId)
  if (!link) {
    return undefined;
  }

  if (!link.url && !isLinkType(link, LINK_TYPES.ADD_TO_CART) && !isLinkType(link, LINK_TYPES.ORDER_WAYS_PRODUCT)) {
    return undefined;
  }

  if (isLinkType(link, LINK_TYPES.ADD_TO_CART)) {
    return {
      children: (
        <ButtonWithAddToCart
          link={link}
          device={device}
          variant={ButtonVariants.TERTIARY}
          size={ButtonSizes.MEDIUM}
          constants={constants}
        />
      )
    };
  }

  if (isLinkType(link, LINK_TYPES.ORDER_WAYS_PRODUCT)) {
    return {
      text: link.title ?? '',
      onClick: () => openDialog({ type: link.id || 'order-ways-product', url: link.url })
    };
  }

  return {
    text: link.title ?? '',
    onClick: () => navigateTo(link.url)
  };
}

/**
 * Transforms a single `GroupedCardItemNode` into a `GroupedCardItem` view model.
 *
 * @param item - Raw item node from the data layer
 * @param device - Current device type, used for image sizing and link filtering
 * @param constants - Optional i18n constants for slider accessibility labels
 * @param openDialog - Dialog store action used for order-ways product links
 */
function transformItem(
  item: GroupedCardItemNode,
  device: Device,
  constants: Constant | undefined,
  openDialog: DialogStore['openDialog']
): GroupedCardItem {
  const imageXofY = constants?.i18n_image_x_of_y;
  const text =
    item.text && !isRichTextEmpty(item.text) ? (
      <RichTextBody text={item.text} variant={TEXT_VARIANT.BODY} />
    ) : undefined;
  const media =
    item.images && item.images.length > 0
      ? item.images.map((image, index) => (
          <Image key={index} image={image} loading={IMAGE_LOADING.LAZY} device={device} trim={IMAGE_TRIM.NONE} />
        ))
      : undefined;
  const secondaryMedia = item.secondaryMedia ? (
    <Image image={item.secondaryMedia} loading={IMAGE_LOADING.LAZY} device={device} />
  ) : undefined;
  const mediaAddons = item.featureFlag?.topLeft
    ? {
        topLeft: {
          label: item.featureFlag.topLeft.label,
          icon: item.featureFlag.topLeft.iconName ? getIconByName(item.featureFlag.topLeft.iconName) : undefined
        }
      }
    : undefined;
  const sliderAccessibility = constants
    ? {
        ariaLabel: [item.headline, item.subheadline, item.anchor].filter(Boolean).join(', '),
        slideLabel: imageXofY
          ? (index: number, total: number) =>
              [item.headline, item.subheadline, item.anchor, parseSlideLabelTemplate(imageXofY)(index, total)]
                .filter(Boolean)
                .join(', ')
          : undefined,
        dotsGroupLabel: constants.i18n_select_images,
        prevButtonLabel: constants.i18n_previous_image_frame,
        nextButtonLabel: constants.i18n_next_image_frame
      }
    : undefined;
  const link = item.links.length > 0 ? item.links[0] : undefined;
  const isProductActive = item.productData ? item.productData.status === PRODUCT_ATTRIBUTE_STATUS_VALUES.ACTIVE : true;
  const action = isProductActive ? getActionObject(link, device, constants, openDialog) : undefined;

  return {
    id: item.id,
    subheadline: item.subheadline,
    supportingText: item.supportingText,
    media,
    secondaryMedia,
    mediaAddons,
    text,
    action,
    sliderAccessibility
  };
}

/**
 * Maps `GroupedCardItemNode` data models to `GroupedCardItem` view models consumed by the
 * `@pixum/grouped-card` component. Resolves images, rich text, feature flags, slider
 * accessibility labels, and the primary action (add-to-cart, order-ways dialog, or navigation).
 *
 * @param items - Raw item nodes from the data layer
 * @param device - Current device type, used for image sizing and link filtering
 * @param constants - Optional i18n constants for slider accessibility labels
 * @param openDialog - Dialog store action used for order-ways product links
 */
function transformItems(
  items: GroupedCardItemNode[],
  device: Device,
  constants: Constant | undefined,
  openDialog: DialogStore['openDialog']
): GroupedCardItem[] {
  return items.map(item => transformItem(item, device, constants, openDialog));
}

export function GroupedCardComponent(props: GroupedCardComponentProps) {
  const { groupedCard, device, constants } = props;
  const openDialog = useDialogStore(state => state.openDialog);

  const [activeIndex, setActiveIndex] = useState(0);

  const items = transformItems(groupedCard.items, device, constants, openDialog);
  const chips = groupedCard.chips && groupedCard.chips.length > 0 ? { chips: groupedCard.chips } : undefined;

  const groupedCardProps: GroupedCardProps = {
    id: groupedCard.id,
    headline: groupedCard.headline,
    items,
    chips,
    activeIndex,
    onActiveIndexChange: setActiveIndex
  };

  return <GroupedCard {...groupedCardProps} />;
}
